import {
  AccountCenter,
  type AiNameAnalysisState,
  AnalyticsConsent,
  ApiRequestError,
  AuthPage,
  AuthPanel,
  analyzeName,
  ComplianceReportForm,
  createGeneration,
  downloadNameCard,
  type GenerationRequest,
  getLegalDocument,
  LEGAL_PATHS,
  LegalDocumentContent,
  type LegalPath,
  loadFigure,
  loadGeneration,
  loadMe,
  loadNameAnalysis,
  loadSiteConfig,
  loadSurname,
  loadSurnames,
  type MeResponse,
  type NameAnalysis,
  NameAnalysisEnhancement,
  openConsentSettings,
  PurchaseModal,
  ResetPasswordPanel,
  type ResultName,
  regenerate,
  type SiteConfigResponse,
  type SurnameFigure,
  type SurnameRecord,
  TurnstileWidget,
  toggleFavorite,
  trackEvent,
  useCountUp,
  useInView,
  usePointerParallax,
} from "@jieyi/web-shared";
import {
  ArrowRight,
  BadgeCheck,
  BookMarked,
  Check,
  CircleAlert,
  Download,
  Globe2,
  Heart,
  Languages,
  LoaderCircle,
  RefreshCw,
  ScanLine,
  ShoppingBag,
  Sparkles,
  Volume2,
} from "lucide-react";
import { type CSSProperties, type FormEvent, useCallback, useEffect, useState } from "react";

function Header() {
  return (
    <header className="site-header">
      <a className="brand" href="/">
        <span className="brand-mark">結</span>
        <span>
          JIEYI<small>CHINESE NAME STUDIO</small>
        </span>
      </a>
      <nav>
        <a href="/#studio">Name studio</a>
        <a href="/#method">Our method</a>
        <a href="/surnames">Surnames</a>
        <a href="/meaning">Name meaning</a>
        <a href="/pricing">Pricing</a>
        <a href="https://name.jieyiai.dev">
          <Languages size={14} />
          中文
        </a>
      </nav>
      <AuthPanel language="en" compact />
    </header>
  );
}

function Footer() {
  return (
    <footer>
      <div className="footer-brand">
        <span className="brand-mark">結</span>
        <div>
          <strong>JIEYI</strong>
          <p>A Chinese name should feel lived-in, not translated.</p>
        </div>
      </div>
      <div className="footer-links">
        <a href="/terms">Terms</a>
        <a href="/privacy">Privacy</a>
        <a href="/acceptable-use">AIGC Policy</a>
        <a href="/ai-disclosure">AI Models</a>
        <a href="/refund">Refunds</a>
        <a href="/cookies">Cookies</a>
        <button className="text-button" type="button" onClick={openConsentSettings}>
          Privacy settings
        </button>
        <a href="/report">Contact & Report</a>
      </div>
      <small>© 2026 Jieyi AI · Cultural and creative reference only</small>
      <AnalyticsConsent language="en" />
    </footer>
  );
}

function LegalPage({ path }: { path: LegalPath }) {
  const document = getLegalDocument("en", path);
  return (
    <>
      <Header />
      <main className="legal-page">
        <LegalDocumentContent document={document} />
        <a href="/" className="primary-button">
          Return home
          <ArrowRight />
        </a>
      </main>
      <Footer />
    </>
  );
}

function ReportPage() {
  return (
    <>
      <Header />
      <main className="legal-page">
        <ComplianceReportForm language="en" />
      </main>
      <Footer />
    </>
  );
}

function PricingPage() {
  const [cardRef, cardInView] = useInView<HTMLElement>();
  const [config, setConfig] = useState<SiteConfigResponse | null>(null);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const refreshMe = useCallback(async () => setMe(await loadMe()), []);
  const closePurchase = useCallback(() => setPurchaseOpen(false), []);
  useEffect(() => {
    void Promise.all([loadSiteConfig(), loadMe()]).then(([siteConfig, account]) => {
      setConfig(siteConfig);
      setMe(account);
    });
  }, []);
  const openPurchase = () => {
    if (!me?.user) {
      window.location.href = "/login";
      return;
    }
    setPurchaseOpen(true);
  };
  return (
    <>
      <Header />
      <main className="pricing-page">
        <section className={cardInView ? "reveal in-view" : "reveal"} ref={cardRef}>
          <span className="kicker">ONE CLEAR PRICE</span>
          <h1>
            Five names.
            <br />
            From $4.99.
            <br />
            No subscription.
          </h1>
          <div className="price-line">
            <b>$4.99</b>
            <span>per generation</span>
          </div>
          <p className="pricing-pack-note">Ten for $39.99 · $4.00 each · Save $9.91</p>
          <ul>
            <li>
              <Check />
              Five culturally natural names
            </li>
            <li>
              <Check />
              Pronunciation and character meanings
            </li>
            <li>
              <Check />
              Real classical source references
            </li>
            <li>
              <Check />
              Automatic credit return if generation fails
            </li>
          </ul>
          <p className="model-disclosure">
            AI models: DeepSeek V4 Flash, with DeepSeek V4 Pro as a retry fallback.
            <a href="/ai-disclosure">Read the model and safety disclosure</a>
          </p>
          <button className="primary-button" type="button" onClick={openPurchase}>
            Choose a credit pack
            <ArrowRight />
          </button>
        </section>
      </main>
      <Footer />
      <PurchaseModal
        language="en"
        open={purchaseOpen}
        products={config?.products ?? []}
        paymentEnabled={config?.features.payment ?? false}
        onClose={closePurchase}
        onRefresh={refreshMe}
      />
    </>
  );
}

function SurnamePage() {
  const [items, setItems] = useState<SurnameRecord[]>([]);
  const [query, setQuery] = useState("");
  useEffect(() => {
    void loadSurnames().then(setItems);
  }, []);
  async function search(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setItems(await loadSurnames(query));
  }
  return (
    <>
      <Header />
      <main className="directory-page">
        <span className="kicker">THE SURNAME INDEX</span>
        <h1>A family name is the first cultural signal.</h1>
        <p>Over 300 surnames on file. Open any entry for origin, famous bearers, and reach.</p>
        <form className="directory-search" onSubmit={search}>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search a character or pinyin"
          />
          <button className="primary-button" type="submit">
            Search
          </button>
        </form>
        <div className="surname-grid">
          {items.map((item) => (
            <a
              className="surname-card"
              href={`/surnames/${encodeURIComponent(item.hanzi)}`}
              key={item.hanzi}
            >
              <b>{item.hanzi}</b>
              <div>
                <h2>{item.pinyin}</h2>
                <p>{item.meaningEn}</p>
                <small>
                  {item.rank
                    ? `COMMON SURNAME REFERENCE #${item.rank}`
                    : "TRADITIONAL CHINESE SURNAME"}
                </small>
              </div>
              <span className="surname-more">DOSSIER →</span>
            </a>
          ))}
        </div>
      </main>
      <Footer />
    </>
  );
}

function SurnameDetailPage({ hanzi }: { hanzi: string }) {
  const [surname, setSurname] = useState<SurnameRecord | null>(null);
  const [error, setError] = useState("");
  const valid = /^[\u3400-\u9fff]{1,2}$/.test(hanzi);
  const [bodyRef, bodyInView] = useInView<HTMLDivElement>();
  useEffect(() => {
    if (!valid) {
      setError("Invalid surname format");
      return;
    }
    void loadSurname(hanzi)
      .then(setSurname)
      .catch(() => setError("Surname not found"));
  }, [hanzi, valid]);
  return (
    <>
      <Header />
      <main className="surname-detail">
        {error ? (
          <div className="surname-detail-error">
            <h1>{error}</h1>
            <a className="outline-button" href="/surnames">
              Back to the index
            </a>
          </div>
        ) : !surname ? (
          <div className="surname-detail-loading">Pulling the name file…</div>
        ) : (
          <div
            className={bodyInView ? "surname-body reveal in-view" : "surname-body reveal"}
            ref={bodyRef}
          >
            <header className="surname-hero">
              <div className="surname-hero-main">
                <span className="kicker">SURNAME DOSSIER</span>
                <b className="surname-hero-glyph">{surname.hanzi}</b>
                <div className="surname-hero-meta">
                  <h1>{surname.pinyin}</h1>
                  {surname.rank ? (
                    <span className="surname-rank">Common #{surname.rank}</span>
                  ) : null}
                </div>
              </div>
              <p className="surname-meaning">{surname.meaningEn}</p>
            </header>
            <div className="surname-sections">
              <section>
                <h2>Origin</h2>
                <p>{surname.originEn ?? "Detailed origin is being prepared."}</p>
              </section>
              {surname.storyEn ? (
                <section className="story">
                  <h2>The story</h2>
                  <p>{surname.storyEn}</p>
                </section>
              ) : null}
              {surname.figures?.length ? (
                <section>
                  <h2>Famous bearers</h2>
                  <div className="figure-chips">
                    {surname.figures.map((figure) => (
                      <a
                        className="figure-chip"
                        href={`/figures/${encodeURIComponent(figure.name)}`}
                        key={figure.name}
                      >
                        <b>{figure.nameEn ?? figure.name}</b>
                        {figure.titleEn ? <i>{figure.titleEn}</i> : null}
                      </a>
                    ))}
                  </div>
                </section>
              ) : (
                <section>
                  <h2>Famous bearers</h2>
                  <p>{surname.famousEn ?? "No notable bearers on file yet."}</p>
                </section>
              )}
              {surname.distributionEn ? (
                <section>
                  <h2>Where it lives</h2>
                  <p>{surname.distributionEn}</p>
                </section>
              ) : null}
            </div>
            <div className="surname-actions">
              <a className="primary-button" href="/#studio">
                Get my {surname.hanzi} name
              </a>
              <a className="outline-button" href="/surnames">
                Back to the index
              </a>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}

function FigurePage({ name }: { name: string }) {
  const [data, setData] = useState<{
    figure: SurnameFigure;
    surname: SurnameRecord;
    siblings: SurnameFigure[];
  } | null>(null);
  const [error, setError] = useState("");
  const valid = /^[\u3400-\u9fff]{2,4}$/.test(name);
  const [bodyRef, bodyInView] = useInView<HTMLDivElement>();
  useEffect(() => {
    if (!valid) {
      setError("Invalid name format");
      return;
    }
    void loadFigure(name)
      .then(setData)
      .catch(() => setError("Figure not found"));
  }, [name, valid]);
  return (
    <>
      <Header />
      <main className="surname-detail figure-detail">
        {error ? (
          <div className="surname-detail-error">
            <h1>{error}</h1>
            <a className="outline-button" href="/surnames">
              Back to the index
            </a>
          </div>
        ) : !data ? (
          <div className="surname-detail-loading">Opening the dossier…</div>
        ) : (
          <div
            className={bodyInView ? "surname-body reveal in-view" : "surname-body reveal"}
            ref={bodyRef}
          >
            <header className="surname-hero">
              <div className="surname-hero-main">
                <span className="kicker">FIGURE DOSSIER</span>
                <b className="surname-hero-glyph">{data.figure.name}</b>
                <div className="surname-hero-meta">
                  {data.figure.nameEn ? <h1>{data.figure.nameEn}</h1> : null}
                  <div className="figure-tags">
                    {data.figure.titleEn ? (
                      <span className="figure-title">{data.figure.titleEn}</span>
                    ) : null}
                    {data.figure.era ? <span className="figure-era">{data.figure.era}</span> : null}
                    <a
                      className="figure-surname-link"
                      href={`/surnames/${encodeURIComponent(data.figure.surnameHanzi)}`}
                    >
                      {data.figure.surnameHanzi} surname file →
                    </a>
                  </div>
                </div>
              </div>
              {data.figure.introEn ? (
                <p className="surname-meaning">{data.figure.introEn}</p>
              ) : (
                <p className="surname-meaning">
                  {data.figure.titleEn
                    ? `A noted bearer of the ${data.figure.surnameHanzi} surname: ${data.figure.titleEn}.`
                    : "Full biography is being prepared."}
                </p>
              )}
            </header>
            {data.siblings?.length ? (
              <div className="surname-sections">
                <section>
                  <h2>More {data.figure.surnameHanzi} bearers</h2>
                  <div className="figure-chips">
                    {data.siblings.map((sibling) => (
                      <a
                        className="figure-chip"
                        href={`/figures/${encodeURIComponent(sibling.name)}`}
                        key={sibling.name}
                      >
                        <b>{sibling.nameEn ?? sibling.name}</b>
                        {sibling.titleEn ? <i>{sibling.titleEn}</i> : null}
                      </a>
                    ))}
                  </div>
                </section>
              </div>
            ) : null}
            <div className="surname-actions">
              <a className="primary-button" href="/#studio">
                Get my {data.figure.surnameHanzi} name
              </a>
              <a
                className="outline-button"
                href={`/surnames/${encodeURIComponent(data.figure.surnameHanzi)}`}
              >
                Open the {data.figure.surnameHanzi} file
              </a>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}

function MeaningPage() {
  const [name, setName] = useState("");
  const [analysis, setAnalysis] = useState<NameAnalysis | null>(null);
  const [ai, setAi] = useState<AiNameAnalysisState | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function pollAi(requestId: string) {
    for (let count = 0; count < 60; count += 1) {
      const current = await loadNameAnalysis(requestId);
      setAi(current);
      if (current.status === "completed" || current.status === "failed") return;
      await new Promise((resolve) => window.setTimeout(resolve, 2000));
    }
    setAi({ status: "failed", error: "Analysis timed out" });
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setBusy(true);
    try {
      const response = await analyzeName(name);
      setAnalysis(response.analysis);
      setAi(response.ai);
      if (response.ai.status === "queued" && response.ai.requestId)
        await pollAi(response.ai.requestId);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not analyse this name");
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <Header />
      <main className="meaning-page">
        <div className="meaning-layout">
          <div className="meaning-intro">
            <span className="kicker">NAME MEANING LOOKUP</span>
            <h1>Already have a Chinese name?</h1>
            <p className="meaning-lede">
              No invented origins — a character-by-character breakdown, the verified literary
              source, and the practical watch-outs, in one file.
            </p>
            <form className="directory-search" onSubmit={submit}>
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                maxLength={4}
                placeholder="Enter 2–4 Chinese characters"
                required
              />
              <button className="primary-button" disabled={busy} type="submit">
                {busy ? "Analysing…" : "Analyse"}
              </button>
            </form>
            {error && <p className="form-error">{error}</p>}
            <ul className="meaning-points">
              <li>
                <ScanLine />
                Character-by-character meanings
              </li>
              <li>
                <BookMarked />
                Verified classical source quotes
              </li>
              <li>
                <BadgeCheck />
                Rare-character and usage watch-outs
              </li>
            </ul>
          </div>
          <div className="meaning-panel">
            {!analysis && (
              <aside className="meaning-sample" aria-hidden="true">
                <div className="meaning-sample-head">
                  <span>Sample analysis</span>
                  <small>Enter a name to see yours</small>
                </div>
                <h2>林晚晴</h2>
                <p>Surname: 林 · Given name: 晚晴</p>
                <div className="character-grid">
                  <div>
                    <b>晚</b>
                    <span>Dusk — unhurried, composed</span>
                  </div>
                  <div>
                    <b>晴</b>
                    <span>Clear skies — bright after rain</span>
                  </div>
                </div>
                <blockquote>
                  “天意怜幽草，人间重晚晴”
                  <cite>Tang · Li Shangyin, Evening Brightness</cite>
                </blockquote>
              </aside>
            )}
            {analysis && (
              <section className="meaning-result">
                <h2>{analysis.fullName}</h2>
                <p>
                  Surname: {analysis.surname} · Given name: {analysis.givenName}
                </p>
                <div className="character-grid">
                  {analysis.characters.map((item) => (
                    <div key={item.character}>
                      <b>{item.character}</b>
                      <span>{item.meaningEn}</span>
                    </div>
                  ))}
                </div>
                {analysis.source && (
                  <blockquote>
                    “{analysis.source.quote}”
                    <cite>
                      {analysis.source.dynasty} · {analysis.source.author}, {analysis.source.title}
                    </cite>
                  </blockquote>
                )}
                <p>{analysis.note}</p>
                <p>
                  {analysis.risks.length
                    ? `Watch-outs: ${analysis.risks.join("; ")}`
                    : "No obvious rare-character or negative-character risk found."}
                </p>
                <NameAnalysisEnhancement analysis={analysis} ai={ai} language="en" />
              </section>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}

function ResultCard({
  name,
  requestId,
  index,
}: {
  name: ResultName;
  requestId: string;
  index: number;
}) {
  const [favorite, setFavorite] = useState(false);
  const score = useCountUp(name.score, 1200);
  return (
    <article className="result-card" style={{ "--i": index } as CSSProperties}>
      <span className="card-number">0{index + 1}</span>
      <div className="name-identity">
        <div className="hanzi-name">{name.fullName}</div>
        <div>
          <h3>{name.pinyin}</h3>
          <button type="button" className="pronounce">
            <Volume2 />
            {name.pronunciation}
          </button>
        </div>
      </div>
      <button
        type="button"
        className={favorite ? "heart active" : "heart"}
        aria-label="Save name"
        onClick={async () => setFavorite(await toggleFavorite(requestId, name.fullName))}
      >
        <Heart />
      </button>
      <button
        type="button"
        className="download-card"
        aria-label="Download name card"
        onClick={() => downloadNameCard(name, "en")}
      >
        <Download />
      </button>
      <p className="recommendation">{name.recommendation}</p>
      <div className="character-grid">
        {name.characters.map((character) => (
          <div key={character.character}>
            <b>{character.character}</b>
            <span>{character.meaning}</span>
          </div>
        ))}
      </div>
      {name.originalNameConnection && (
        <div className="connection">
          <ScanLine />
          <div>
            <small>CONNECTION TO YOUR NAME</small>
            <p>{name.originalNameConnection}</p>
          </div>
        </div>
      )}
      <blockquote>
        “{name.source.quote}”
        <cite>
          {name.source.dynasty} · {name.source.author}, {name.source.title}
        </cite>
      </blockquote>
      <details>
        <summary>Open the cultural notes</summary>
        <dl>
          <dt>Meaning</dt>
          <dd>{name.overallMeaning}</dd>
          <dt>Sound & form</dt>
          <dd>{name.soundAndForm}</dd>
          <dt>Best for</dt>
          <dd>{name.usage}</dd>
          <dt>Watch-outs</dt>
          <dd>{name.risks.length ? name.risks.join("; ") : "No obvious issue found"}</dd>
        </dl>
      </details>
      <div className="score">
        <strong>{score}</strong>
        <span>CULTURAL FIT</span>
      </div>
    </article>
  );
}

function NamingStudio({
  config,
  me,
  refreshMe,
  onPurchase,
}: {
  config: SiteConfigResponse | null;
  me: MeResponse | null;
  refreshMe: () => Promise<void>;
  onPurchase: () => void;
}) {
  const [token, setToken] = useState("");
  const [task, setTask] = useState<GenerationRequest | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [studioRef, studioInView] = useInView<HTMLElement>();
  const onToken = useCallback((value: string) => setToken(value), []);

  useEffect(() => {
    const resultId = new URLSearchParams(window.location.search).get("result");
    if (resultId && me?.user) {
      void loadGeneration(resultId)
        .then(setTask)
        .catch(() => setError("Could not load this name file."));
    }
  }, [me?.user]);

  async function poll(requestId: string) {
    for (let count = 0; count < 90; count += 1) {
      const current = await loadGeneration(requestId);
      setTask(current);
      if (current.status === "completed" || current.status === "failed_refunded") {
        await refreshMe();
        return current;
      }
      await new Promise((resolve) => window.setTimeout(resolve, 2000));
    }
    throw new Error("Generation is still running. Check your account history in a moment.");
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    if (!me?.user) {
      setError("Sign in before submitting your naming profile.");
      return;
    }
    if (me.credits < 1) {
      onPurchase();
      return;
    }
    const form = new FormData(event.currentTarget);
    const list = (key: string) =>
      String(form.get(key) ?? "")
        .split(/[,\s]+/)
        .filter(Boolean);
    const input = {
      originalName: String(form.get("originalName") ?? ""),
      pronunciation: String(form.get("pronunciation") ?? ""),
      gender: String(form.get("gender") ?? "any"),
      ageRange: String(form.get("ageRange") ?? "prefer-not"),
      nativeLanguage: String(form.get("nativeLanguage") ?? ""),
      personality: String(form.get("personality") ?? ""),
      scenario: String(form.get("scenario") ?? "daily"),
      strategy: String(form.get("strategy") ?? "balanced"),
      style: String(form.get("style") ?? "modern"),
      preferredCharacters: list("preferredCharacters"),
      avoidedCharacters: list("avoidedCharacters"),
      includeSurname: form.get("includeSurname") === "on",
    };
    setBusy(true);
    try {
      trackEvent("naming_started", { site: "en", mode: "new" });
      const requestId = await createGeneration(input, token);
      setTask({
        id: requestId,
        site: "en",
        status: "queued",
        errorCode: null,
        errorMessage: null,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        result: null,
      });
      await refreshMe();
      const completed = await poll(requestId);
      if (completed.status === "completed")
        trackEvent("naming_completed", { site: "en", mode: "new" });
    } catch (reason) {
      if (reason instanceof ApiRequestError && reason.status === 402) {
        onPurchase();
        return;
      }
      setError(reason instanceof Error ? reason.message : "Could not start generation");
    } finally {
      setBusy(false);
    }
  }

  async function regenerateInDirection(direction: string) {
    if (!task) return;
    if ((me?.credits ?? 0) < 1) {
      onPurchase();
      return;
    }
    setBusy(true);
    setError("");
    try {
      trackEvent("naming_started", { site: "en", mode: "regenerate" });
      const requestId = await regenerate(task.id, direction);
      setTask({
        id: requestId,
        site: "en",
        status: "queued",
        errorCode: null,
        errorMessage: null,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        result: null,
      });
      await refreshMe();
      const completed = await poll(requestId);
      if (completed.status === "completed")
        trackEvent("naming_completed", { site: "en", mode: "regenerate" });
    } catch (reason) {
      if (reason instanceof ApiRequestError && reason.status === 402) {
        onPurchase();
        return;
      }
      setError(reason instanceof Error ? reason.message : "Could not regenerate names");
    } finally {
      setBusy(false);
    }
  }

  if (task?.status === "completed" && task.result) {
    return (
      <section className="results" id="studio">
        <div className="results-intro">
          <span className="kicker">YOUR CHINESE NAME FILE</span>
          <h2>
            Five names,
            <br />
            each with a life of its own.
          </h2>
          <p>{task.result.summary}</p>
          <button type="button" className="outline-button" onClick={() => setTask(null)}>
            <RefreshCw />
            Start a new profile
          </button>
          <div className="direction-buttons">
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                regenerateInDirection("Make the next names sound closer to my original name")
              }
            >
              Closer in sound
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                regenerateInDirection("Make the next names feel more culturally Chinese")
              }
            >
              More Chinese
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => regenerateInDirection("Make the next names modern and easy to use")}
            >
              More modern
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                regenerateInDirection("Make the next names more classical and literary")
              }
            >
              More classical
            </button>
          </div>
        </div>
        <div className="result-list">
          {task.result.names.map((name, index) => (
            <ResultCard key={name.fullName} name={name} requestId={task.id} index={index} />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="studio" id="studio" ref={studioRef}>
      <div className={studioInView ? "studio-aside reveal in-view" : "studio-aside reveal"}>
        <span className="section-index">02 / NAME STUDIO</span>
        <h2>
          Tell us how your name should <i>feel</i>.
        </h2>
        <p>
          We balance sound, meaning, cultural fluency, and the situations where you will actually
          use it.
        </p>
        <div className="balance">
          <ShoppingBag />
          <span>Available generations</span>
          <strong>{me?.credits ?? 0}</strong>
        </div>
        {me?.user ? (
          <button
            type="button"
            className="outline-button"
            disabled={!config?.features.payment}
            onClick={onPurchase}
          >
            {config?.features.payment ? "Buy generation credits" : "Payments coming soon"}
          </button>
        ) : (
          <AuthPanel language="en" />
        )}
      </div>
      <form
        className={[
          "profile-form",
          studioInView ? "reveal in-view" : "reveal",
          task && task.status !== "failed_refunded" ? "thinking" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onSubmit={submit}
      >
        <div className="field-pair">
          <label>
            Your name
            <input required name="originalName" placeholder="Michael, Amélie, Arjun…" />
          </label>
          <label>
            How it sounds
            <input name="pronunciation" placeholder="MY-kəl or /mi.ka.el/" />
          </label>
        </div>
        <div className="field-pair three">
          <label>
            Gender
            <select name="gender" defaultValue="any">
              <option value="any">No preference</option>
              <option value="male">Masculine</option>
              <option value="female">Feminine</option>
              <option value="neutral">Neutral</option>
            </select>
          </label>
          <label>
            Age
            <select name="ageRange" defaultValue="prefer-not">
              <option value="prefer-not">Prefer not to say</option>
              <option value="under18">Under 18</option>
              <option value="18-29">18–29</option>
              <option value="30-49">30–49</option>
              <option value="50plus">50+</option>
            </select>
          </label>
          <label>
            Native language
            <input required name="nativeLanguage" placeholder="English" />
          </label>
        </div>
        <div className="field-pair">
          <label>
            Naming strategy
            <select name="strategy" defaultValue="balanced">
              <option value="balanced">Balanced: sound + meaning</option>
              <option value="phonetic">Sound like my name</option>
              <option value="meaning">Carry my name's meaning</option>
            </select>
          </label>
          <label>
            Style
            <select name="style" defaultValue="modern">
              <option value="modern">Modern & clear</option>
              <option value="classic">Classical</option>
              <option value="elegant">Elegant</option>
              <option value="strong">Strong</option>
              <option value="gentle">Gentle</option>
              <option value="scholarly">Scholarly</option>
              <option value="neutral">Quietly neutral</option>
            </select>
          </label>
        </div>
        <label>
          Personality, work, and interests
          <textarea
            name="personality"
            rows={4}
            maxLength={500}
            placeholder="I am a curious product designer who loves mountains, calm spaces, and learning languages…"
          />
        </label>
        <div className="field-pair">
          <label>
            Where will you use it?
            <select name="scenario" defaultValue="daily">
              <option value="daily">Daily life</option>
              <option value="workplace">Workplace</option>
              <option value="social">Social media</option>
              <option value="study">Study</option>
              <option value="travel">Travel</option>
            </select>
          </label>
          <label className="check-label">
            <input type="checkbox" name="includeSurname" defaultChecked />
            <span>Include a natural Chinese surname</span>
          </label>
        </div>
        <div className="field-pair">
          <label>
            Characters you like
            <input name="preferredCharacters" placeholder="安, 宁, 晨" />
          </label>
          <label>
            Characters to avoid
            <input name="avoidedCharacters" placeholder="伟, 强" />
          </label>
        </div>
        <TurnstileWidget
          siteKey={config?.features.turnstileSiteKey ?? ""}
          onToken={onToken}
          theme="dark"
        />
        {error && (
          <p className="form-error">
            <CircleAlert />
            {error}
          </p>
        )}
        {task && task.status !== "failed_refunded" && (
          <div className="task-state" role="status">
            <span className="task-dots" aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
            <span key={task.status} className="task-text">
              {task.status === "retrying"
                ? "DeepSeek is busy. Retrying safely…"
                : task.status === "queued"
                  ? "Your profile is queued…"
                  : "Curating and explaining your names…"}
            </span>
          </div>
        )}
        {task?.status === "failed_refunded" && (
          <p className="form-error">
            <CircleAlert />
            {task.errorMessage ?? "Generation failed. Your credit was restored."}
          </p>
        )}
        <button type="submit" className="primary-button generate-button" disabled={busy}>
          {busy ? (
            <>
              <LoaderCircle className="spin" />
              Building your name file
            </>
          ) : (
            <>
              <Sparkles />
              Create five Chinese names
            </>
          )}
        </button>
        <small>
          One successful generation uses one credit. Failed generations restore it automatically.
        </small>
      </form>
    </section>
  );
}

function AccountPage() {
  return (
    <>
      <Header />
      <main>
        <AccountCenter language="en" />
      </main>
      <Footer />
    </>
  );
}

function HomePage() {
  const [config, setConfig] = useState<SiteConfigResponse | null>(null);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const refreshMe = useCallback(async () => setMe(await loadMe()), []);
  const openPurchase = useCallback(() => setPurchaseOpen(true), []);
  const closePurchase = useCallback(() => setPurchaseOpen(false), []);
  const passportRef = usePointerParallax<HTMLDivElement>();
  const [methodRef, methodInView] = useInView<HTMLElement>();
  const [worldNoteRef, worldNoteInView] = useInView<HTMLElement>();
  useEffect(() => {
    void loadSiteConfig().then(setConfig);
    void refreshMe();
  }, [refreshMe]);
  useEffect(() => {
    if ((me?.credits ?? 0) > 0) setPurchaseOpen(false);
  }, [me?.credits]);
  return (
    <>
      <Header />
      <main>
        <section className="hero">
          <div className="coordinate">
            31.2304° N<br />
            121.4737° E
          </div>
          <div className="hero-copy">
            <span className="kicker">A NAME BETWEEN CULTURES</span>
            <h1>
              Your name,
              <br />
              at home in <i>Chinese.</i>
            </h1>
            <p>
              Not a mechanical transliteration. We craft names that sound natural to Chinese ears
              and still carry something recognisably yours.
            </p>
            <a className="primary-button" href="#studio">
              Find my Chinese name
              <ArrowRight />
            </a>
          </div>
          <div className="hero-passport" ref={passportRef}>
            <div className="passport-top">
              <span>NAME FILE</span>
              <span>CN / 文化通行证</span>
            </div>
            <div className="passport-name">
              <b>林</b>
              <b>清和</b>
            </div>
            <p>LÍN QĪNG HÉ</p>
            <div className="passport-meta">
              <div>
                <small>ORIGIN</small>
                <span>Michael</span>
              </div>
              <div>
                <small>CHARACTER</small>
                <span>Calm · Clear</span>
              </div>
            </div>
            <div className="passport-stamp">
              自然
              <br />
              NATURAL
            </div>
          </div>
          <div className="vertical-note">SOUND × MEANING × CULTURE</div>
        </section>
        <section className="promise">
          <p>Five considered names</p>
          <span>•</span>
          <p>Character-by-character meaning</p>
          <span>•</span>
          <p>From $4.99</p>
        </section>
        <NamingStudio config={config} me={me} refreshMe={refreshMe} onPurchase={openPurchase} />
        <section className="method" id="method" ref={methodRef}>
          <div className={methodInView ? "method-heading reveal in-view" : "method-heading reveal"}>
            <span className="section-index">03 / OUR METHOD</span>
            <h2>
              A Chinese name is not a costume.
              <br />
              It should survive a real introduction.
            </h2>
          </div>
          <div
            className={
              methodInView ? "method-grid reveal-cascade in-view" : "method-grid reveal-cascade"
            }
          >
            <article>
              <span>01</span>
              <ScanLine />
              <h3>We listen</h3>
              <p>
                Your original name, its pronunciation, meaning, and the person behind it form the
                brief.
              </p>
            </article>
            <article>
              <span>02</span>
              <BookMarked />
              <h3>We ground it</h3>
              <p>
                Curated characters, natural surname patterns, and traceable literary references
                build the candidate set.
              </p>
            </article>
            <article>
              <span>03</span>
              <BadgeCheck />
              <h3>We test it</h3>
              <p>
                DeepSeek selects within strict boundaries. We then re-check sound, awkward meanings,
                rarity, and daily use.
              </p>
            </article>
          </div>
        </section>
        <section
          className={worldNoteInView ? "world-note reveal in-view" : "world-note reveal"}
          ref={worldNoteRef}
        >
          <Globe2 />
          <h2>
            Made for the moment someone asks,
            <br />
            <em>“What does your Chinese name mean?”</em>
          </h2>
          <p>You will have a real answer.</p>
        </section>
      </main>
      <Footer />
      <PurchaseModal
        language="en"
        open={purchaseOpen}
        products={config?.products ?? []}
        paymentEnabled={config?.features.payment ?? false}
        onClose={closePurchase}
        onRefresh={refreshMe}
      />
    </>
  );
}

export default function App() {
  const path = window.location.pathname;
  if (LEGAL_PATHS.has(path as LegalPath)) return <LegalPage path={path as LegalPath} />;
  if (path === "/report") return <ReportPage />;
  if (path === "/pricing") return <PricingPage />;
  if (path === "/login")
    return (
      <>
        <Header />
        <main>
          <AuthPage language="en" />
        </main>
        <Footer />
      </>
    );
  if (path.startsWith("/figures/"))
    return <FigurePage name={decodeURIComponent(path.slice("/figures/".length))} />;
  if (path.startsWith("/surnames/"))
    return <SurnameDetailPage hanzi={decodeURIComponent(path.slice("/surnames/".length))} />;
  if (path === "/surnames") return <SurnamePage />;
  if (path === "/meaning") return <MeaningPage />;
  if (path === "/account") return <AccountPage />;
  if (path === "/reset-password")
    return (
      <>
        <Header />
        <main className="reset-page">
          <ResetPasswordPanel language="en" />
        </main>
        <Footer />
      </>
    );
  return <HomePage />;
}
